import { useEffect, useState } from 'react'
import PersonalInformationForm from './PersonalInformationForm';
import PersonalInformationView from './PersonalInformationView';
import VolunteerismInformationForm from './VolunteerismInformationForm';
import VolunteerismInformationView from './VolunteerismInformationView';
import SectionCard from './SectionCard';
import ChecklistFooter from './CheckListFooter';
import ProfExperienceForm from "./ProfExperienceForm";

import ProfExperienceView from "./Experience/ProfExperienceView";
import CheckListHeader from './CheckListHeader';
import { message } from 'antd';
import commonLookupService from 'src/services/commonLookupService';
import { recordError } from 'src/utils/logService';
import volunteerService from '../../services/volunteerScientistService';
import dayjs from 'dayjs';
import { useVolScienceRetrieval } from "./hooks/useVolScienceRetrieval";

import {
  Messages,
  Notify,
  openNotification,
  util
} from '@seims/shared';

// Define unique page states for clarity
const PAGE_DASHBOARD = "dashboard";
const PAGE_PERSONAL_INFO_FORM = "personalInfoForm";
const PAGE_PROF_EXP_FORM = "professionalExperienceForm";
const PAGE_VOLUNTEERISM_FORM = "volunteerismInformationForm";


export default function VolunteerScientistsDetailsView() {
  // Overall app page state
  const [currentPage, setCurrentPage] = useState(PAGE_DASHBOARD);

  // Data states for each section
  const [personalInfoData, setPersonalInfoData] = useState(null);
  const [professionalExperienceData, setProfessionalExperienceData] =
    useState(null);
  const [volunteerismInfoData, setVolunteerismInfoData] = useState(null);
  const [volunteerRoles, setVolunteerRoles] = useState([]);
  const [volunteerReasons, setVolunteerReasons] = useState([]);
  // State to track completed sections for the checklist
  const [completedSections, setCompletedSections] = useState({
    personalInfo: false,
    professionalExperience: false,
    volunteerismInformation: false,
  });


  const [submissionId, setSubmissionId] = useState(0);
  const [formStatus, setFormStatus] = useState({});
  const { volunteerScientistProfile } = useVolScienceRetrieval();

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, [currentPage]); // Re-run this effect whenever the page changes

  useEffect(() => {

    const fetchData = async () => {
      setIsLoading(true);
      try {
        await getProfile();
        await getVolunteerRoles();
        await getVolunteerReasons();
      } catch (error) {
        recordError(error);
        message.error("Failed to load all profile data.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [volunteerScientistProfile.data, submissionId]);

  // Effect to update isSubmitted state when formStatus changes
  useEffect(() => {
    const isSubmitted = !formStatus.submissionAllowed && !formStatus.displaySubmissionButton;
    setIsSubmitted(isSubmitted);
  }, [formStatus]);

  // Effect to update completedSections whenever data changes
  useEffect(() => {
    setCompletedSections({
      personalInfo: !!personalInfoData,
      professionalExperience: !!professionalExperienceData,
      volunteerismInformation: !!volunteerismInfoData,
    });
  }, [personalInfoData, professionalExperienceData, volunteerismInfoData, formStatus]);


  const getProfile = async () => {
    try {

      const response = await volunteerScientistProfile?.data;
      if (response) {
        if (response?.demographics) {
          fetchPersonalInfo(response.demographics);
        }
        if (response?.volunteerism) {
          fetchVolunteerismInfo(response.volunteerism);
        }
        if (response?.professionalExperience) {
          setProfessionalExperienceData(response.professionalExperience);
        }
        setFormStatus({
          readOnly: response.readOnly,
          submissionAllowed: response.submissionAllowed,
          displaySubmissionButton: response.submissionButtonDisplayed
        })
      }
    } catch (error) {
      recordError(error);
    }
  };

  const getVolunteerRoles = async () => {
    try {
      const response = await commonLookupService.getVolunteerRoles();
      if (response) {
        setVolunteerRoles(response);
      }
    } catch (error) {
      recordError(error);
    }
  };

  const getVolunteerReasons = async () => {
    try {
      const response = await commonLookupService.getVolunteerMotivations();
      if (response) {
        setVolunteerReasons(response);
      }
    } catch (error) {
      recordError(error);
    }
  };

  const fetchPersonalInfo = (savedData) => {

    try {
      if (savedData) {
        // Transform fetched data for display, similar to your save logic
        const displayReadyData = {
          id: savedData.id,
          firstName: savedData.firstName,
          lastName: savedData.lastName,
          contactNumber: savedData.contactNumber,
          email: savedData.email,
          dateOfBirth: savedData.dateOfBirth
            ? dayjs(savedData.dateOfBirth).format("YYYY-MM-DD")
            : null,
          title: savedData.title || null,
          gender: savedData.gender || null,
          race: savedData.race || null,
          disability: savedData.disability || null,
          languages: Array.isArray(savedData.languages)
            ? savedData.languages.filter(Boolean)
            : [],
          location: (() => {
            const newLocation = {
              province: null,
              district: null,
              town: null,
            };
            // Safely assign if the nested objects exist
            if (savedData.location?.province) {
              newLocation.province = savedData.location.province;
            }
            if (savedData.location?.district) {
              newLocation.district = savedData.location.district;
            }
            if (savedData.location?.town) {
              newLocation.town = savedData.location.town;
            }
            return newLocation;
          })(),
          profilePicture: savedData.profilePicture,
          volunteerDifferentLocation: savedData.volunteerDifferentLocation,
          preferEmail: savedData.preferEmail ?? false,
          preferPhone: savedData.preferPhone ?? false,
        };
        setPersonalInfoData(displayReadyData);
      } else {
        setPersonalInfoData(null);
      }
    } catch (error) {
      console.error("Error fetching personal information:", error);
      setPersonalInfoData(null);
    }
  };

  const fetchVolunteerismInfo = (savedData) => {
    try {
      if (savedData) {
        // Transform fetched data for display, similar to your save logic
        const displayReadyData = {
          preferredRoles: Array.isArray(savedData.preferredRoles)
            ? savedData.preferredRoles.filter(Boolean)
            : [],
          reasons: Array.isArray(savedData.reasons)
            ? savedData.reasons.filter(Boolean)
            : [],
          id: savedData.id,
          otherReason: savedData.otherReason || "",
          otherRole: savedData.otherRole || "",
        };

        setVolunteerismInfoData(displayReadyData);
      } else {
        setVolunteerismInfoData(null);
      }
    } catch (error) {
      console.error("Error fetching volunteerism information:", error);
      setVolunteerismInfoData(null);
    }
  };

  const handleSavePersonalInfo = async (formData) => {
    setIsLoading(true);
    try {
      let ACTION = "Personal Information was saved successfully";
      const response = await volunteerService.saveDemographics(formData);

      if (response && response.config?.method == "put") {
        ACTION = "Personal Information was updated successfully";
      }
      setPersonalInfoData(response?.data);
      openNotification("Personal Information", ACTION, Notify.SUCCESS);

      setCurrentPage("dashboard");
    } catch (error) {
      console.error("Failed to save personal information:", error);
      // --- Handle 409 Conflict specifically ---
      if (error.response.status === 409) {
        openNotification(
          "Add Personal Information",
          error.response.data.message,
          Notify.WARNING
        );
      } else {
        message.error(
          `Failed to save personal information: ${error.message || "An unknown error occurred."
          }`
        );
      }
    } finally {
      setIsLoading(false); // Stop loading after saving (success or error)
    }
  };

  const handleEditPersonalInfo = () => {
    setCurrentPage(PAGE_PERSONAL_INFO_FORM); // Go to personal info form
  };

  const handleAddPersonalInfo = () => {
    const defaultData = { ...personalInfoData, id: 0 };
    setCurrentPage(PAGE_PERSONAL_INFO_FORM);
    setPersonalInfoData(defaultData);
  };

  const handleCancelPersonalInfoForm = () => {
    if (personalInfoData?.id === 0) {
      setPersonalInfoData(null);
    }
    setCurrentPage(PAGE_DASHBOARD); // Always return to dashboard on cancel
  };

  // Placeholder Callbacks for other sections (you'll implement these fully later)
  const handleAddProfessionalExperience = () => {
    const defaultData = { ...professionalExperienceData, id: 0 };

    setCurrentPage(PAGE_PROF_EXP_FORM);
    setProfessionalExperienceData(defaultData);
  };

  /* Start Section for Volunteerism */
  const handleSaveVolunteerismInfor = async (data) => {
    setIsLoading(true);
    try {
      let ACTION = "Volunteerism Information was saved successfully";

      const response = await volunteerService.saveVolunteerism(
        data.volunteerism
      );

      if (response && response.config?.method == "put") {
        ACTION = "Volunteerism Information was updated successfully";
      }
      setVolunteerismInfoData(response?.data);

      openNotification("Volunteerism Information", ACTION, Notify.SUCCESS);

      setCurrentPage("dashboard");
    } catch (error) {
      console.error("Failed to save volunteerism information:", error);
      // --- Handle 409 Conflict specifically ---
      if (error.response.status === 409) {
        openNotification(
          "Add Volunteerism Information",
          error.response.data.message,
          Notify.WARNING
        );
      } else {
        message.error(
          `Failed to save volunteerism information: ${error.message || "An unknown error occurred."
          }`
        );
      }
    } finally {
      setIsLoading(false); // Stop loading after saving (success or error)
    }
  };

  const handleAddVolunteerismInformation = () => {
    const defaultData = { ...volunteerismInfoData, id: 0 };
    setVolunteerismInfoData(defaultData); // Clear data if starting new
    setCurrentPage(PAGE_VOLUNTEERISM_FORM); // Example: navigate to volunteer form
  };

  const handleEditVolunteerismInfor = () => {
    setCurrentPage(PAGE_VOLUNTEERISM_FORM); // Go to volunteerism info form
  };

  const handleCancelVolunteerismInfo = () => {
    if (volunteerismInfoData?.id == 0) {
      setVolunteerismInfoData(null);
    }
    // setCurrentPage('volunteerismInfoView'); // Switch back to view mode
    setCurrentPage(PAGE_DASHBOARD); // Always return to dashboard on cancel
  };
  /* End Section for Volunteerism */

  const handleSubmitApplication = async () => {
    setIsLoading(true);

    try {
      const finalResponse = await volunteerService.saveFinal();
      openNotification(
        "Volunteer Scientist",
        "Volunteer Scientist registered successfuly",
        Notify.SUCCESS
      );
      const savedDataId = finalResponse.data.id;
      setSubmissionId(savedDataId);
      window.scrollTo(0, 0);
      formStatus.submissionAllowed = false
      formStatus.displaySubmissionButton = false

    } catch (error) {
      // --- Handle 409 Conflict specifically ---
      if (error.response.status === 409) {
        openNotification(
          "Volunteer Scientist",
          error.response.data.message,
          Notify.WARNING
        );
      } else {
        openNotification(
          "Volunteer Scientist",
          `Failed to register volunteer scientist: ${error.message || "An unknown error occurred."
          }`,
          Notify.ERROR
        );
      }
    } finally {
      setIsLoading(false); // Stop loading after submission
    }
  };

  const handleSaveExperience = async (values) => {
    setIsLoading(true);

    let msg = util.parseMessage(
      Messages.ADD_SUCCESS,
      "Professional Experience"
    );

    let ACTION = "Professional Experience";

    try {
      const response = await volunteerService.saveProfessionalExperience(
        values
      );
      if (response && response.config?.method == "put") {
        ACTION = "Professional Experience";
        msg = ACTION + ' was updated successfully'
      }
      setProfessionalExperienceData(response?.data);

      openNotification(ACTION, msg, Notify.SUCCESS);
      setCurrentPage(PAGE_DASHBOARD);
    } catch (error) {
      if (error.response.status === 409) {
        let msg = util.parseMessage(
          Messages.ADD_FAILURE_DUPLICATE,
          "Professional Experience"
        );
        openNotification("Professional Experience", msg, Notify.ERROR);
        return;
      }


      if (error.response.status === 400) {
        let msg = util.parseMessage(
          Messages.INVALID_FIELD,
          "Professional Experience"
        );
        openNotification(
          "Add/Update Professional Experience",
          msg,
          Notify.ERROR
        );
      }
    } finally {
      setIsLoading(false); // Stop loading after submission
    }
  };

  const handleCancelExperience = () => {
    if (professionalExperienceData?.id == 0) {
      setProfessionalExperienceData(null);
    }
    setCurrentPage(PAGE_DASHBOARD);
  };

  const handleProfExperienceEdit = () => {
    setCurrentPage(PAGE_PROF_EXP_FORM);
  };

  const hasData = (data, vsSection) => {
    if (vsSection == "PROFESSIONAL EXPERIENCE")
      return data && data.qualifications?.length > 0;
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        padding: "5px",
        marginBottom: "20px"
      }}
    >
      {currentPage === PAGE_DASHBOARD && (
        <>
          <CheckListHeader
            completedSections={completedSections}
            onSubmitApplication={handleSubmitApplication}
            finalId={submissionId}
            formStatus={formStatus}
          />
          {/* Personal Information Section */}
          <SectionCard
            title="PERSONAL INFORMATION"
            description="Lorem ipsum dolor sit amet, consectetur adipiscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua. At vero eos et accusam et justo duo dolores et ea rebum. Stet clita kasd gubergren."
            icon={require("../../assets/contract.png")} // Placeholder icon based on image_cc085b.png
            buttonText="Add Personal Information"
            onAddClick={handleAddPersonalInfo}
            hasData={!!personalInfoData} // Check if personalInfoData exists
          >
            {personalInfoData && (
              <PersonalInformationView
                userData={personalInfoData}
                onEdit={handleEditPersonalInfo}
              />
            )}
          </SectionCard>

          <SectionCard
            title="PROFESSIONAL EXPERIENCE"
            description="Professional experience for a scientist typically involves conducting research, analyzing data, and developing new knowledge within a specific scientific field. This can include working in laboratories, conducting fieldwork, collaborating with other researchers, and presenting findings at conferences or in publications. "
            icon={require("../../assets/analysis.png")} // Placeholder icon based on image_cc085b.png
            buttonText="Add Professional Experience"
            onAddClick={handleAddProfessionalExperience}
            hasData={!!professionalExperienceData} // Check if personalInfoData exists
          >
            {hasData(professionalExperienceData, "PROFESSIONAL EXPERIENCE") && (
              <ProfExperienceView
                userData={professionalExperienceData}
                onEdit={handleProfExperienceEdit}
              />
            )}
          </SectionCard>

          {/* Volunteerism Information Section*/}
          <SectionCard
            title="VOLUNTEERISM INFORMATION"
            description="Lorem ipsum dolor sit amet, consectetur adipiscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua. At vero eos et accusam et justo duo dolores et ea rebum. Stet clita kasd gubergren."
            icon={require("../../assets/solidarity.png")} // Placeholder icon
            buttonText="Add Volunteerism Information"
            onAddClick={handleAddVolunteerismInformation}
            hasData={!!volunteerismInfoData}
          >
            {/* This is where your Volunteerism Information View/Form would go */}
            {volunteerismInfoData && (
              <VolunteerismInformationView
                volunteerismData={volunteerismInfoData}
                onEdit={handleEditVolunteerismInfor}
              />
            )}
          </SectionCard>

          {/* Checklist Footer */}
          {!isSubmitted && (
            <ChecklistFooter
              completedSections={completedSections}
              onSubmitApplication={handleSubmitApplication}
              finalId={submissionId}
              formStatus={formStatus}
            />
          )}
        </>
      )}

      {currentPage === "personalInfoForm" && (
        <PersonalInformationForm
          onSave={handleSavePersonalInfo}
          initialValues={personalInfoData} // Pass existing data for editing
          onCancel={handleCancelPersonalInfoForm}
        />
      )}

      {currentPage === PAGE_VOLUNTEERISM_FORM && (
        <VolunteerismInformationForm
          onSave={handleSaveVolunteerismInfor}
          initialValues={volunteerismInfoData} // Pass existing data for editing
          onCancel={handleCancelVolunteerismInfo}
          volunteerRoles={volunteerRoles}
          volunteeringReasons={volunteerReasons}
        />
      )}

      {currentPage === PAGE_PROF_EXP_FORM && (
        <ProfExperienceForm
          onSave={handleSaveExperience}
          initialValues={professionalExperienceData} // Pass existing data for editing
          onCancel={handleCancelExperience}
        />
      )}
    </div>
  );
}
